import { brandColor } from '../../../_helpers/brand-color';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, skip } from 'rxjs';
import { ChartData, ChartOptions } from 'chart.js';

import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { StatsFilterService } from '../statistics-filter.service';
import {
  StatsDataService,
  PatientMetrics,
  TimeSeriesPoint,
  CohortData,
  CategoryData,
} from '../statistics-data.service';

// Components
import { KpiCardComponent } from '../_components/kpi-card/kpi-card.component';
import { StatChartComponent } from '../_components/stat-chart/stat-chart.component';

// PrimeNG
import { TableModule } from 'primeng/table';

@Component({
  standalone: true,
  selector: 'app-stats-patients',
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    KpiCardComponent,
    StatChartComponent,
    TableModule,
  ],
})
export class StatsPatientsComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  private subscriptions: Subscription[] = [];

  // Loading
  public loadingMetrics: boolean = true;
  public loadingGrowth: boolean = true;
  public loadingCohorts: boolean = true;
  public loadingAge: boolean = true;

  // Data
  public metrics: PatientMetrics | null = null;
  public growthData: TimeSeriesPoint[] = [];
  public cohorts: CohortData[] = [];
  public ageDistribution: CategoryData[] = [];

  // Charts
  public growthChartData: ChartData | null = null;
  public growthChartOptions: ChartOptions = {};
  public ageChartData: ChartData | null = null;
  public segmentChartData: ChartData | null = null;
  public segmentChartOptions: ChartOptions = {};

  constructor(
    public session: SessionService,
    private filterService: StatsFilterService,
    private dataService: StatsDataService,
    private translate: TranslateService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadData();

    this.subscriptions.push(
      this.filterService.filters$
        .pipe(skip(1))
        .subscribe(() => this.loadData()),
    );

    this.subscriptions.push(
      this.translate.onLangChange.subscribe(() => {
        this.buildGrowthChart();
        this.buildAgeChart();
        this.buildSegmentChart();
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  loadData(): void {
    const customerId = this.session.getCustomer()?.id;
    const siteId = this.session.getSite()?.id;

    if (!customerId || !siteId) return;

    // Metrics
    this.loadingMetrics = true;
    this.dataService.getPatientMetrics(customerId, siteId).subscribe({
      next: (data) => {
        this.metrics = data;
        this.buildSegmentChart();
        this.loadingMetrics = false;
      },
      error: () => (this.loadingMetrics = false),
    });

    // Growth
    this.loadingGrowth = true;
    this.dataService.getPatientGrowth(customerId, siteId).subscribe({
      next: (data) => {
        this.growthData = data;
        this.buildGrowthChart();
        this.loadingGrowth = false;
      },
      error: () => (this.loadingGrowth = false),
    });

    // Cohorts
    this.loadingCohorts = true;
    this.dataService.getCohortRetention(customerId, siteId).subscribe({
      next: (data) => {
        this.cohorts = data;
        this.loadingCohorts = false;
      },
      error: () => (this.loadingCohorts = false),
    });

    // Age Distribution
    this.loadingAge = true;
    this.dataService.getPatientAgeDistribution(customerId, siteId).subscribe({
      next: (data) => {
        this.ageDistribution = data;
        this.buildAgeChart();
        this.loadingAge = false;
      },
      error: () => (this.loadingAge = false),
    });
  }

  private buildGrowthChart(): void {
    const labels = this.growthData.map((p) => p.label || p.date);
    const values = this.growthData.map((p) => p.value);

    this.growthChartData = {
      labels,
      datasets: [
        {
          label: this.translate.instant(
            'STATISTICS.PATIENTS.CHARTS.GROWTH.DATASET',
          ),
          data: values,
          fill: true,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4,
          pointRadius: 3,
          pointBackgroundColor: '#10b981',
        },
      ],
    };

    this.growthChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
        y: {
          grid: { color: '#f1f5f9' },
          ticks: { color: '#64748b', font: { size: 11 } },
          beginAtZero: true,
        },
      },
    };
  }

  private buildAgeChart(): void {
    this.ageChartData = {
      labels: this.ageDistribution.map((a) => a.name),
      datasets: [
        {
          label: this.translate.instant(
            'STATISTICS.PATIENTS.CHARTS.AGE_DISTRIBUTION.DATASET',
          ),
          data: this.ageDistribution.map((a) => a.count),
          backgroundColor: '#8b5cf6',
          borderRadius: 4,
        },
      ],
    };
  }

  private buildSegmentChart(): void {
    if (!this.metrics) return;

    this.segmentChartData = {
      labels: [
        this.translate.instant('STATISTICS.PATIENTS.CHARTS.SEGMENTATION.NEW'),
        this.translate.instant(
          'STATISTICS.PATIENTS.CHARTS.SEGMENTATION.RETURNING',
        ),
      ],
      datasets: [
        {
          data: [this.metrics.newPatients, this.metrics.returningPatients],
          backgroundColor: ['#f59e0b', brandColor()],
          borderWidth: 0,
        },
      ],
    };

    this.segmentChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y',
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#1e293b',
          padding: 12,
          cornerRadius: 8,
        },
      },
      scales: {
        x: {
          grid: { color: '#f1f5f9' },
          ticks: { color: '#64748b', font: { size: 11 } },
          beginAtZero: true,
        },
        y: {
          grid: { display: false },
          ticks: { color: '#64748b', font: { size: 11 } },
        },
      },
    };
  }

  getCohortCellClass(value: number): string {
    if (value >= 50) return 'cohort-cell--high';
    if (value >= 30) return 'cohort-cell--medium';
    if (value >= 10) return 'cohort-cell--low';
    return 'cohort-cell--none';
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat(this.getLocale(), {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  }

  private getLocale(): string {
    const lang = (this.translate.currentLang || 'es').toLowerCase();
    if (lang.startsWith('en')) return 'en-US';
    if (lang.startsWith('ca')) return 'ca-ES';
    return 'es-ES';
  }
}
